import React from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import { Person } from "../../assets/icons";
import { useDarkMode } from "../../context/DarkModeContext";
import {
  InviteIcon,
  SupportIcon,
  UserIcon,
  OrderIcon,
  DarkModeIcon,
  LogoutIcon,
} from "../../assets/icons";
import { logout } from "../../features/store/authSlice";
import { useAppDispatch, useAppSelector } from "../../hooks/hooks";
import useClickOutside from "../../hooks/useClickOutside";
import { MenuItemProps, ProfileMenuProps } from "./types";
import { convertCurrency, formatCurrency } from "../../utils/utils";

const ProfileMenu: React.FC<ProfileMenuProps> = ({
  user,
  setIsOpen,
  profileBtnRef,
}) => {
  const { toggleDarkMode } = useDarkMode();
  const { t } = useTranslation();
  const translate = t as unknown as (key: string) => string;
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { data: appData } = useAppSelector((state) => state.appData);
  const { selectedCurrency } = useAppSelector((state) => state.locale);

  const handleLogout = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      dispatch(logout());

      // Close the dropdown
      setIsOpen(false);

      // Redirect to home page
      navigate("/");
    } catch (error) {
      console.error("Logout failed:", error);
      // toast.error(t('logout.error'));
    }
  };

  const handleNavigation = (path: string) => {
    setIsOpen(false);
    navigate(path);
  };

  const profileMenuRef = useClickOutside(
    () => {
      setIsOpen(false);
    },
    true,
    profileBtnRef,
  );
  return (
    <div
      ref={profileMenuRef}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => {
        e.stopPropagation();
      }}
      className=" lg:h-[481px] w-full bg-white dark:bg-black rounded-3xl shadow-lg overflow-hidden z-50 mt-8"
    >
      <div className="p-2 lg:p-4">
        {/* User Info Section */}
        <div className="flex gap-2 text-[10px] bg-[#F4F4F5] dark:bg-[#FFFFFF1A] p-0  rounded-[24px] w-full h-[45px]">
          <div className="flex items-center gap-[12px] py-[10px] px-[14px]">
            <img
              src={user?.image_url || "/png/default.png"}
              alt="Profile"
              style={{ width: 32, height: 32 }}
              className="size-28 sm:size-40 rounded-full p-1 bg-profPixBG aspect-square object-cover"
            />
            <div>
              <p className="text-lg font-semibold dark:text-white">
                {user?.firstname || "Guest"}
              </p>
              <p className="text-xs dark:text-white">
                {user?.email || "guest@gmail.com"}
              </p>
            </div>
          </div>
        </div>
        <div className="flex gap-2 bp:hidden mt-2">
          {/* Wallet Balance */}
          <button
            className="relative w-full flex items-center rounded-lg p-2 overflow-hidden"
            style={{
              backgroundImage: `url(/images/bg.png)`,
              backgroundPosition: "center",
              backgroundSize: "cover",
            }}
          >
            <div className="flex items-center gap-2">
              <img
                src="/png/dg.png"
                alt={translate("wallet.title")}
                className="w-[24px] h-[24px] object-contain"
              />
              <div className="flex flex-col items-start">
                <span className="text-sm font-bold text-white font-lt leading-tight">
                  {formatCurrency(user?.dg_coin_wallet_balance)}
                </span>
                <p className="text-white text-sm">
                  {selectedCurrency?.symbol || "$"}
                  {formatCurrency(
                    convertCurrency(
                      user?.dg_coin_wallet_balance,
                      "Coins",
                      selectedCurrency?.symbol || "$",
                      appData.currencies,
                    ),
                  )}
                </p>
              </div>
            </div>
          </button>

          {/* Point Balance */}
          <button className="relative w-full flex items-center rounded-lg p-2 overflow-hidden bg-[#FFF4EA]">
            <div className="flex items-center gap-2">
              <img
                src="/images/diamond.png"
                alt={translate("wallet.title")}
                className="w-[24px] h-[24px] object-contain"
              />
              <div className="flex flex-col items-start">
                <span className="text-sm font-bold text-[#CE8B41] font-lt leading-tight">
                  {formatCurrency(user?.dg_point_wallet_balance)}
                </span>
                <p className="text-[#CE8B41] text-sm opacity-90">
                  {selectedCurrency?.symbol || "$"}
                  {formatCurrency(
                    convertCurrency(
                      Number(user?.dg_point_wallet_balance) || 0,
                      "Points",
                      selectedCurrency?.symbol || "$",
                      appData.currencies,
                    ),
                  )}
                </p>
              </div>
            </div>
          </button>
        </div>

        {/* Menu Items */}
        <div className="dark:text-white">
          <div className=" border-b border-border-1 dark:border-tabs">
            {/* <Link
              to="/account"
            > */}
            <MenuItem
              onClick={() => handleNavigation("/account")}
              icon={<UserIcon />}
              text={translate("profileMenu.accountDetails")}
            />

            <MenuItem
              onClick={() => handleNavigation("/order-history")}
              icon={<OrderIcon />}
              text={translate("profileMenu.orderHistory")}
            />
            {/* </Link> */}
          </div>
          <div onClick={toggleDarkMode}>
            <MenuItem
              icon={<DarkModeIcon />}
              text={translate("profileMenu.darkMode")}
            />
          </div>

          <MenuItem
            icon={<InviteIcon />}
            text={translate("profileMenu.inviteFriend")}
          />
          <MenuItem
            onClick={() => handleNavigation("/support")}
            icon={<SupportIcon />}
            text={translate("profileMenu.support")}
          />
        </div>

        {/* Homepage Banner */}
        <div
          className="m-2 text-white rounded-lg h-[72px] overflow-hidden"
          style={{
            backgroundImage: `url(/images/bg.png)`,
            // backgroundPosition: "center top",
            backgroundSize: "cover",
            position: "relative",
          }}
        >
          <div
            className="p-4 flex justify-between items-center relative z-20"
            onClick={() => handleNavigation("/")}
          >
            <div>
              <h3 className="text-sm font-bold mb-1 w-50">
                {translate("profileMenu.visitHomepage")}
              </h3>
              <p className="text-white text-sm">
                {translate("profileMenu.readMoreInfo")}{" "}
              </p>
            </div>
            <div className="text-2xl">›</div>
          </div>
          {/* </Link> */}
        </div>

        {/* Logout */}
        <div className="px-4 pb-4">
          <div onClick={handleLogout}>
            <MenuItem icon={<LogoutIcon />} text="Logout" />
          </div>
        </div>
      </div>
    </div>
  );
};

// Icon Components

// Menu Item Component

const MenuItem: React.FC<MenuItemProps> = ({ icon, text, onClick }) => {
  return (
    <div
      className="flex items-center space-x-2 lg:space-x-4 p-2 lg:p-4 cursor-pointer"
      onClick={onClick}
    >
      <div className="dark:text-white text-[10px]">{icon}</div>
      <span className="dark:text-white text-[#667085] text-sm">{text}</span>
    </div>
  );
};

export default ProfileMenu;
